import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _decorate from "@babel/runtime/helpers/esm/decorate";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _get from "@babel/runtime/helpers/esm/get";
let _ = t => t,
  _t;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _superPropGet(t, e, r, o) { var p = _get(_getPrototypeOf(1 & o ? t.prototype : t), e, r); return 2 & o ? function (t) { return p.apply(r, t); } : p; }
/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import styles from "././button.css.js";
import StableSelectorMixin from "../../globals/mixins/stable-selector.js";
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';
import CTAMixin, { ariaLabels, icons } from "../../component-mixins/cta/cta.js";
import CDSButton from '@carbon/web-components/es/components/button/button.js';
import { CTA_TYPE } from "../cta/defs.js";
const {
  prefix,
  stablePrefix: c4dPrefix
} = settings;

/**
 * Button.
 *
 * @element c4d-button
 * @csspart button - The button. Usage: `c4d-button::part(button)`
 * @csspart hidden-paragraph - The hidden paragraph that contains the link. Usage: `c4d-button::part(hidden-paragraph)`
 * @csspart hidden-icon-span - The span element inside the hidden paragraph. Usage: `c4d-button::part(hidden-icon-span)`
 * @csspart visually-hidden-span - The visually hidden span element for accessibility. Usage: `c4d-button::part(visually-hidden-span)`
 */
let C4DButton = _decorate([customElement(`${c4dPrefix}-button`)], function (_initialize, _CTAMixin) {
  class C4DButton extends _CTAMixin {
    constructor(...args) {
      super(...args);
      _initialize(this);
    }
  }
  return {
    F: C4DButton,
    d: [{
      kind: "field",
      decorators: [query('a')],
      key: "_linkNode",
      value: void 0
    }, {
      kind: "field",
      decorators: [query(`slot[name='icon']`)],
      key: "iconSlot",
      value: void 0
    }, {
      kind: "field",
      decorators: [property()],
      key: "span",
      value: void 0
    }, {
      kind: "field",
      decorators: [property({
        type: Boolean,
        reflect: true
      })],
      key: "isExpressive",
      value() {
        return true;
      }
    }, {
      kind: "method",
      key: "_handleDisabledClick",
      value:
      /**
       * `true` if expressive theme enabled.
       */

      function _handleDisabledClick(event) {
        _superPropGet(C4DButton, "_handleClick", this, 3)([event]);
      }

      /**
       * TODO: Due to the new render() logic coming from the CWC v2 button,
       * this function is currently unused. We'd need to dynamically add it.
       *
       * @returns The icon for the print styles
       */
    }, {
      kind: "method",
      key: "_renderIconPrintStyles",
      value: function _renderIconPrintStyles() {
        return html(_t || (_t = _`
      <p
        class="${0}--btn--hidden"
        aria-hidden="true"
        part="hidden-paragraph">
        <span part="hidden-icon-span">:</span> ${0}
      </p>
      <slot name="icon"></slot>
    `), prefix, this.href);
      }

      /**
       * @returns The template for the icon.
       */
    }, {
      kind: "method",
      key: "_renderButtonIcon",
      value: function _renderButtonIcon() {
        var _icons, _icon;
        const {
          ctaType
        } = this;
        const icon = (_icons = icons[`${ctaType}-${document.dir}`]) !== null && _icons !== void 0 ? _icons : icons[ctaType];
        return `
        <span class="${prefix}--visually-hidden" part="visually-hidden-span">${ariaLabels[ctaType]}</span>
        ${icon === null || icon === void 0 || (_icon = icon()) === null || _icon === void 0 || (_icon = _icon.strings) === null || _icon === void 0 ? void 0 : _icon.join()}
      `;
      }

      /**
       * Handles button video title
       *
       * @param event The event.
       */
      // @ts-ignore: The decorator refers to this method but TS thinks this method is not referred to
    }, {
      kind: "field",
      key: "_handleVideoTitleUpdate",
      value() {
        return async event => {
          var _event$detail;
          if (event && this.ctaType === CTA_TYPE.VIDEO && this.href === ((_event$detail = event.detail) === null || _event$detail === void 0 ? void 0 : _event$detail.videoId)) {
            var _this$textContent;
            const {
              videoDuration,
              videoName
            } = event.detail;
            const {
              formatVideoDuration,
              formatVideoCaption
            } = this;
            const formattedVideoDuration = formatVideoDuration({
              duration: !videoDuration ? videoDuration : videoDuration * 1000
            });
            this.videoDuration ? null : this.videoDuration = formattedVideoDuration;
            this.videoTitle = formatVideoCaption({
              duration: formattedVideoDuration,
              name: videoName
            });
            if (((_this$textContent = this.textContent) === null || _this$textContent === void 0 ? void 0 : _this$textContent.trim()) === '') {
              const title = document.createTextNode(this.videoTitle);
              this.appendChild(title);
            }
          }
        };
      }
    }, {
      kind: "method",
      key: "connectedCallback",
      value: function connectedCallback() {
        _superPropGet(C4DButton, "connectedCallback", this, 3)([]);
        const {
          eventRequestAdditionalVideoData
        } = this.constructor;
        document.addEventListener(eventRequestAdditionalVideoData, this._handleVideoTitleUpdate);
      }
    }, {
      kind: "method",
      key: "disconnectedCallback",
      value: function disconnectedCallback() {
        _superPropGet(C4DButton, "disconnectedCallback", this, 3)([]);
        const {
          eventRequestAdditionalVideoData
        } = this.constructor;
        document.removeEventListener(eventRequestAdditionalVideoData, this._handleVideoTitleUpdate);
      }
    }, {
      kind: "method",
      key: "updated",
      value: function updated(changedProperties) {
        _superPropGet(C4DButton, "updated", this, 3)([changedProperties]);
        const updateIconForProperties = ['ctaType', 'disabled', 'tooltipText', 'href'];
        const {
          iconSlot
        } = this;

        // Note that the parent may render a different <slot name="icon">
        // based on changes to either disabled, tooltipText, or href, so we make
        // sure to re-render the icon if any of those change, in addition to the
        // ctaType.
        if (iconSlot && updateIconForProperties.some(prop => changedProperties.has(prop))) {
          var _iconSlot$querySelect, _iconSlot$querySelect2;
          (_iconSlot$querySelect = iconSlot.querySelector('svg')) === null || _iconSlot$querySelect === void 0 || _iconSlot$querySelect.remove();
          iconSlot.innerHTML = this._renderButtonIcon();
          (_iconSlot$querySelect2 = iconSlot.querySelector('svg')) === null || _iconSlot$querySelect2 === void 0 || _iconSlot$querySelect2.classList.add(`${prefix}--card__cta`, `${c4dPrefix}-ce--cta__icon`);
        }
      }
    }, {
      kind: "get",
      static: true,
      key: "stableSelector",
      value: function () {
        return `${c4dPrefix}--button`;
      }

      /**
       * The name of the custom event fired when there is a user gesture to run the action.
       */
    }, {
      kind: "get",
      static: true,
      key: "eventRequestAdditionalVideoData",
      value: function () {
        return `${c4dPrefix}-cta-request-additional-video-data`;
      }
    }, {
      kind: "field",
      static: true,
      key: "shadowRootOptions",
      value() {
        return _objectSpread(_objectSpread({}, LitElement.shadowRootOptions), {}, {
          delegatesFocus: true
        });
      }
    }, {
      kind: "field",
      static: true,
      key: "styles",
      value() {
        return styles;
      }
    }]
  };
}, CTAMixin(StableSelectorMixin(CDSButton)));
/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DButton;
//# sourceMappingURL=button.js.map
